import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import Footer from "@/components/Footer";
import { ProcessSteps } from "@/components/marketing";

export default function HowItWorks() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F6F3EC]">
      <SiteNav />

      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-24 pb-20 sm:pt-32 sm:pb-24">
          <p className="eyebrow text-[#C79447] reveal">How FIAD works</p>
          <h1 className="mt-6 font-display text-4xl sm:text-6xl font-semibold tracking-tight leading-[1.05] max-w-4xl reveal reveal-1">A structured path from expertise to evaluation-ready.</h1>
          <p className="mt-8 text-lg sm:text-xl text-[#D4D0C6] leading-relaxed max-w-3xl reveal reveal-2">
            FIAD is built as a six-step pipeline. Each step exists to turn genuine domain expertise into human capability that AI teams can rely on.
          </p>
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24">
          <ProcessSteps dark />
        </div>
      </section>

      <section className="border-b border-white/10 bg-[#0C0C0C]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="eyebrow text-[#71717A]">Rigour & fairness</p>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl font-medium tracking-tight leading-[1.12]">Structured, explainable judgment.</h2>
          </div>
          <div className="lg:col-span-7 lg:pt-12 space-y-5 text-lg text-[#A1A1AA] leading-relaxed">
            <p>FIAD's assessments are designed to be practical and domain-specific — asking experts to demonstrate the actual work rather than answer trivia.</p>
            <p>The goal is judgment that is consistent and explainable: not "this looks good," but a clear account of what works, what doesn't, and why — the kind of feedback that is useful to the teams building AI.</p>
            <p>Eligible contributors are paid competitively for approved project work. FIAD does not guarantee acceptance, project allocation, employment or income, and never charges experts.</p>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-8">
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight max-w-xl">Ready to begin?</h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <button data-testid="how-apply-btn" onClick={() => navigate("/apply")} className="inline-flex items-center justify-center gap-2 bg-[#F6F3EC] text-[#0A0A0A] px-7 py-3.5 text-sm font-medium hover:bg-white transition-colors">Apply as an expert <ArrowRight className="h-4 w-4" /></button>
            <button data-testid="how-ailabs-btn" onClick={() => navigate("/ai-labs")} className="inline-flex items-center justify-center gap-2 border border-white/25 px-7 py-3.5 text-sm font-medium hover:border-white/60 transition-colors">For AI labs</button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
